import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function ProfileEditPage({ adminData, onProfileUpdate }) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        current_password: '',
        new_password: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', content: '' });
    const [errors, setErrors] = useState({});

    const navigate = useNavigate();

    useEffect(() => {
        if (adminData) {
            setFormData({
                name: adminData.name || '',
                email: adminData.email || '',
                current_password: '',
                new_password: ''
            });
        }
    }, [adminData]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (formData.new_password && !formData.current_password) {
            newErrors.current_password = 'Current password is required to change password';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', content: '' });

        if (!validateForm()) {
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');

            const response = await axios.put(
                'https://agpipefitting.org.et/api/admin/update-profile',
                {
                    name: formData.name,
                    current_password: formData.current_password,
                    new_password: formData.new_password
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status === 200) {
                setMessage({ type: 'success', content: 'Profile updated successfully!' });
                onProfileUpdate(response.data.admin);
                setFormData(prev => ({
                    ...prev,
                    current_password: '',
                    new_password: ''
                }));
            }
        } catch (error) {
            console.error('Update Error:', error);
            let errorMsg = 'Profile update failed. Please try again.';

            if (error.response) {
                if (error.response.status === 401) {
                    errorMsg = 'Unauthorized. Please log in again.';
                    localStorage.removeItem('adminToken');
                    setTimeout(() => navigate('/admin-login', { replace: true }), 1000);
                } else if (error.response.data?.errors) {
                    errorMsg = Object.values(error.response.data.errors).flat().join(' ');
                } else if (error.response.data?.message) {
                    errorMsg = error.response.data.message;
                }
            }

            setMessage({ type: 'danger', content: errorMsg });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setErrors(prev => ({ ...prev, [e.target.name]: '' }));
    };

    return (
        <div className="profile-edit-form">
            <h3 className="mb-4">Edit Profile</h3>

            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                        required
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.name}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        readOnly
                        className="bg-light"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                        type="password"
                        name="new_password"
                        value={formData.new_password}
                        onChange={handleChange}
                        placeholder="Enter new password (leave blank to keep current)"
                    />
                    <Form.Text className="text-muted">
                        Minimum 8 characters
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Current Password</Form.Label>
                    <Form.Control
                        type="password"
                        name="current_password"
                        value={formData.current_password}
                        onChange={handleChange}
                        placeholder="Required if changing password"
                        isInvalid={!!errors.current_password}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.current_password}
                    </Form.Control.Feedback>
                </Form.Group>

                {message.content && (
                    <Alert variant={message.type} className="mt-3">
                        {message.content}
                    </Alert>
                )}

                <div className="d-flex justify-content-end gap-2">
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                                <span className="ms-2">Saving...</span>
                            </>
                        ) : 'Save Changes'}
                    </Button>
                </div>
            </Form>
        </div>
    );
}

export default ProfileEditPage;
